import { useState, useRef, useCallback } from 'react'

// Types
interface AddedCandidate {
  id: string
  name: string
  email?: string
  source: 'resume' | 'linkedin' | 'manual' | 'ats'
  fileName?: string
  linkedinUrl?: string
}

interface Role {
  id: string
  title: string
  levelRange: string
}

type ModalStep = 'add' | 'assign' | 'success'
type AddMethod = 'upload' | 'linkedin' | 'manual' | 'ats' | null

interface AddCandidatesModalProps {
  isOpen: boolean
  onClose: () => void
  roles?: Role[]
  hasAtsConnection?: boolean
}

// Icons
function XMarkIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  )
}

function DocumentArrowUpIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m6.75 12l-3-3m0 0l-3 3m3-3v6m-1.5-15H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
    </svg>
  )
}

function LinkIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
    </svg>
  )
}

function UserPlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zM4 19.235v-.11a6.375 6.375 0 0112.75 0v.109A12.318 12.318 0 0110.374 21c-2.331 0-4.512-.645-6.374-1.766z" />
    </svg>
  )
}

function ArrowPathIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
    </svg>
  )
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
    </svg>
  )
}

function SparklesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
    </svg>
  )
}

function BriefcaseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 00.75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 00-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0112 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 01-.673-.38m0 0A2.18 2.18 0 013 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 013.413-.387m7.5 0V5.25A2.25 2.25 0 0013.5 3h-3a2.25 2.25 0 00-2.25 2.25v.894m7.5 0a48.667 48.667 0 00-7.5 0M12 12.75h.008v.008H12v-.008z" />
    </svg>
  )
}

function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
    </svg>
  )
}

function ArrowRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
    </svg>
  )
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
    </svg>
  )
}

// Mock roles for demo
const MOCK_ROLES: Role[] = [
  { id: '1', title: 'Backend Engineer', levelRange: 'Senior–Principal' },
  { id: '2', title: 'Product Designer', levelRange: 'Mid–Senior' },
  { id: '3', title: 'Engineering Manager', levelRange: 'Senior' },
]

export default function AddCandidatesModal({ 
  isOpen, 
  onClose, 
  roles = MOCK_ROLES,
  hasAtsConnection = false 
}: AddCandidatesModalProps) {
  const [step, setStep] = useState<ModalStep>('add')
  const [addMethod, setAddMethod] = useState<AddMethod>(null)
  const [candidates, setCandidates] = useState<AddedCandidate[]>([])
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  
  // Form states
  const [linkedinUrl, setLinkedinUrl] = useState('')
  const [manualName, setManualName] = useState('')
  const [manualEmail, setManualEmail] = useState('')
  
  const fileInputRef = useRef<HTMLInputElement>(null)

  const reset = useCallback(() => {
    setStep('add')
    setAddMethod(null)
    setCandidates([])
    setSelectedRoleId(null)
    setLinkedinUrl('')
    setManualName('')
    setManualEmail('')
  }, [])

  const handleClose = useCallback(() => {
    reset()
    onClose()
  }, [onClose, reset])

  // File handling
  const handleFiles = useCallback((files: FileList | null) => {
    if (!files) return
    
    const newCandidates: AddedCandidate[] = []
    Array.from(files).forEach((file) => {
      if (file.type === 'application/pdf' || 
          file.type === 'application/msword' ||
          file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
        // Extract name from filename (remove extension)
        const name = file.name.replace(/\.(pdf|doc|docx)$/i, '').replace(/[-_]/g, ' ')
        newCandidates.push({
          id: crypto.randomUUID(),
          name: name,
          source: 'resume',
          fileName: file.name,
        })
      }
    })
    
    setCandidates(prev => [...prev, ...newCandidates])
    setAddMethod('upload')
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    handleFiles(e.dataTransfer.files)
  }, [handleFiles])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  // LinkedIn handling
  const handleLinkedinSubmit = useCallback(() => {
    if (!linkedinUrl.trim()) return
    
    // Extract name from LinkedIn URL (basic parsing)
    const urlMatch = linkedinUrl.match(/linkedin\.com\/in\/([^/?]+)/)
    const slug = urlMatch ? urlMatch[1] : 'Unknown'
    const name = slug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    
    setCandidates(prev => [...prev, {
      id: crypto.randomUUID(),
      name,
      source: 'linkedin',
      linkedinUrl: linkedinUrl.trim(),
    }])
    setLinkedinUrl('')
  }, [linkedinUrl])

  // Manual entry handling
  const handleManualSubmit = useCallback(() => {
    if (!manualName.trim()) return
    
    setCandidates(prev => [...prev, {
      id: crypto.randomUUID(),
      name: manualName.trim(),
      email: manualEmail.trim() || undefined,
      source: 'manual',
    }])
    setManualName('')
    setManualEmail('')
  }, [manualName, manualEmail])

  // Remove candidate
  const removeCandidate = useCallback((id: string) => {
    setCandidates(prev => prev.filter(c => c.id !== id))
  }, [])

  // Continue to assignment
  const handleContinueToAssign = useCallback(() => {
    if (candidates.length > 0) {
      setStep('assign')
    }
  }, [candidates])

  // Complete flow
  const handleComplete = useCallback(() => {
    // In production, this would call the API to create candidates
    setStep('success')
  }, [])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-surface-900/60 backdrop-blur-sm transition-opacity"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div 
          className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl transform transition-all animate-scale-in"
          onClick={e => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-2 rounded-lg text-surface-400 hover:text-surface-600 hover:bg-surface-100 transition-colors z-10"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>

          {/* Content */}
          <div className="p-6 sm:p-8">
            {step === 'add' && (
              <AddStep
                addMethod={addMethod}
                setAddMethod={setAddMethod}
                candidates={candidates}
                isDragging={isDragging}
                linkedinUrl={linkedinUrl}
                setLinkedinUrl={setLinkedinUrl}
                manualName={manualName}
                setManualName={setManualName}
                manualEmail={manualEmail}
                setManualEmail={setManualEmail}
                fileInputRef={fileInputRef}
                hasAtsConnection={hasAtsConnection}
                handleFiles={handleFiles}
                handleDrop={handleDrop}
                handleDragOver={handleDragOver}
                handleDragLeave={handleDragLeave}
                handleLinkedinSubmit={handleLinkedinSubmit}
                handleManualSubmit={handleManualSubmit}
                removeCandidate={removeCandidate}
                onContinue={handleContinueToAssign}
              />
            )}

            {step === 'assign' && (
              <AssignStep
                candidates={candidates}
                roles={roles}
                selectedRoleId={selectedRoleId}
                setSelectedRoleId={setSelectedRoleId}
                onBack={() => setStep('add')}
                onComplete={handleComplete}
              />
            )}

            {step === 'success' && (
              <SuccessStep
                candidates={candidates}
                selectedRoleId={selectedRoleId}
                roles={roles}
                onClose={handleClose}
                onAddMore={reset}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// Step 1: Add candidates
interface AddStepProps {
  addMethod: AddMethod
  setAddMethod: (method: AddMethod) => void
  candidates: AddedCandidate[]
  isDragging: boolean
  linkedinUrl: string
  setLinkedinUrl: (url: string) => void
  manualName: string
  setManualName: (name: string) => void
  manualEmail: string
  setManualEmail: (email: string) => void
  fileInputRef: React.RefObject<HTMLInputElement>
  hasAtsConnection: boolean
  handleFiles: (files: FileList | null) => void
  handleDrop: (e: React.DragEvent) => void
  handleDragOver: (e: React.DragEvent) => void
  handleDragLeave: (e: React.DragEvent) => void
  handleLinkedinSubmit: () => void
  handleManualSubmit: () => void
  removeCandidate: (id: string) => void
  onContinue: () => void
}

function AddStep({
  addMethod,
  setAddMethod,
  candidates,
  isDragging,
  linkedinUrl,
  setLinkedinUrl,
  manualName,
  setManualName,
  manualEmail,
  setManualEmail,
  fileInputRef,
  hasAtsConnection,
  handleFiles,
  handleDrop,
  handleDragOver,
  handleDragLeave,
  handleLinkedinSubmit,
  handleManualSubmit,
  removeCandidate,
  onContinue,
}: AddStepProps) {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="pr-8">
        <h2 className="text-2xl font-display font-semibold text-surface-900">
          Add candidates
        </h2>
        <p className="mt-1 text-surface-500">
          Drop resumes, paste LinkedIn profiles, or enter names. We'll take it from there.
        </p>
      </div>

      {/* Method selection - shown when no method selected or always as tabs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MethodButton
          icon={DocumentArrowUpIcon}
          label="Upload"
          sublabel="PDF, DOC"
          isActive={addMethod === 'upload'}
          onClick={() => {
            setAddMethod('upload')
            fileInputRef.current?.click()
          }}
        />
        <MethodButton
          icon={LinkIcon}
          label="LinkedIn"
          sublabel="Paste URL"
          isActive={addMethod === 'linkedin'}
          onClick={() => setAddMethod('linkedin')}
        />
        <MethodButton
          icon={UserPlusIcon}
          label="Manual"
          sublabel="Name only"
          isActive={addMethod === 'manual'}
          onClick={() => setAddMethod('manual')}
        />
        <MethodButton
          icon={ArrowPathIcon}
          label="Import"
          sublabel={hasAtsConnection ? 'From ATS' : 'Connect ATS'}
          isActive={addMethod === 'ats'}
          onClick={() => setAddMethod('ats')}
          disabled={!hasAtsConnection}
        />
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.doc,.docx"
        multiple
        className="hidden"
        onChange={e => handleFiles(e.target.files)}
      />

      {/* Method-specific input areas */}
      <div className="min-h-[160px]">
        {/* Upload drop zone */}
        {(addMethod === 'upload' || addMethod === null) && (
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
            className={`
              relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-200
              ${isDragging 
                ? 'border-brand-400 bg-brand-50 scale-[1.02]' 
                : 'border-surface-300 hover:border-brand-300 hover:bg-surface-50'
              }
            `}
          >
            <DocumentArrowUpIcon className={`w-10 h-10 mx-auto mb-3 ${isDragging ? 'text-brand-500' : 'text-surface-400'}`} />
            <p className="text-surface-700 font-medium">
              {isDragging ? 'Drop files here' : 'Drop resumes or click to browse'}
            </p>
            <p className="text-sm text-surface-500 mt-1">
              PDF or DOC files, up to 10 at a time
            </p>
          </div>
        )}

        {/* LinkedIn input */}
        {addMethod === 'linkedin' && (
          <div className="space-y-3">
            <div className="flex gap-3">
              <input
                type="url"
                value={linkedinUrl}
                onChange={e => setLinkedinUrl(e.target.value)}
                placeholder="https://linkedin.com/in/..."
                onKeyDown={e => e.key === 'Enter' && handleLinkedinSubmit()}
                className="flex-1 px-4 py-3 border-2 border-surface-200 rounded-xl focus:border-brand-500 focus:ring-0 focus:outline-none transition-colors text-surface-900 placeholder:text-surface-400"
                autoFocus
              />
              <button
                onClick={handleLinkedinSubmit}
                disabled={!linkedinUrl.trim()}
                className="px-5 py-3 rounded-xl bg-brand-600 text-white font-medium hover:bg-brand-700 disabled:bg-surface-200 disabled:text-surface-400 disabled:cursor-not-allowed transition-colors"
              >
                <PlusIcon className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-surface-500">
              Paste one or more LinkedIn profile URLs. We'll extract the profile data.
            </p>
          </div>
        )}

        {/* Manual entry */}
        {addMethod === 'manual' && (
          <div className="space-y-3">
            <div className="flex gap-3">
              <input
                type="text"
                value={manualName}
                onChange={e => setManualName(e.target.value)}
                placeholder="Candidate name"
                onKeyDown={e => e.key === 'Enter' && handleManualSubmit()}
                className="flex-1 px-4 py-3 border-2 border-surface-200 rounded-xl focus:border-brand-500 focus:ring-0 focus:outline-none transition-colors text-surface-900 placeholder:text-surface-400"
                autoFocus
              />
              <input
                type="email"
                value={manualEmail}
                onChange={e => setManualEmail(e.target.value)}
                placeholder="Email (optional)"
                onKeyDown={e => e.key === 'Enter' && handleManualSubmit()}
                className="flex-1 px-4 py-3 border-2 border-surface-200 rounded-xl focus:border-brand-500 focus:ring-0 focus:outline-none transition-colors text-surface-900 placeholder:text-surface-400"
              />
              <button
                onClick={handleManualSubmit}
                disabled={!manualName.trim()}
                className="px-5 py-3 rounded-xl bg-brand-600 text-white font-medium hover:bg-brand-700 disabled:bg-surface-200 disabled:text-surface-400 disabled:cursor-not-allowed transition-colors"
              >
                <PlusIcon className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-surface-500">
              Just a name is enough to get started. You can add details later.
            </p>
          </div>
        )}

        {/* ATS import (placeholder) */}
        {addMethod === 'ats' && (
          <div className="border-2 border-dashed border-surface-300 rounded-xl p-8 text-center">
            <ArrowPathIcon className="w-10 h-10 mx-auto mb-3 text-surface-400" />
            <p className="text-surface-700 font-medium">
              {hasAtsConnection ? 'Select candidates from your ATS' : 'Connect your ATS first'}
            </p>
            <p className="text-sm text-surface-500 mt-1">
              {hasAtsConnection 
                ? 'Pull candidates from Greenhouse, Lever, or Ashby' 
                : 'Go to Settings → Integrations to connect your ATS'
              }
            </p>
          </div>
        )}
      </div>

      {/* Added candidates list */}
      {candidates.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-surface-700">
              {candidates.length} candidate{candidates.length !== 1 ? 's' : ''} added
            </h3>
            <button 
              onClick={() => candidates.forEach(c => removeCandidate(c.id))}
              className="text-sm text-surface-500 hover:text-surface-700"
            >
              Clear all
            </button>
          </div>
          <div className="max-h-48 overflow-y-auto space-y-2 pr-1">
            {candidates.map(candidate => (
              <CandidateChip 
                key={candidate.id} 
                candidate={candidate} 
                onRemove={() => removeCandidate(candidate.id)} 
              />
            ))}
          </div>
        </div>
      )}

      {/* Continue button */}
      <div className="flex justify-end pt-2">
        <button
          onClick={onContinue}
          disabled={candidates.length === 0}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-brand-600 text-white font-medium hover:bg-brand-700 disabled:bg-surface-200 disabled:text-surface-400 disabled:cursor-not-allowed transition-colors shadow-sm"
        >
          Continue
          <ArrowRightIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}

// Method selection button
interface MethodButtonProps {
  icon: React.ComponentType<{ className?: string }>
  label: string
  sublabel: string
  isActive: boolean
  onClick: () => void
  disabled?: boolean
}

function MethodButton({ icon: Icon, label, sublabel, isActive, onClick, disabled }: MethodButtonProps) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`
        relative p-4 rounded-xl border-2 transition-all duration-200 text-left group
        ${isActive 
          ? 'border-brand-500 bg-brand-50 ring-2 ring-brand-100' 
          : disabled
            ? 'border-surface-200 bg-surface-50 opacity-50 cursor-not-allowed'
            : 'border-surface-200 hover:border-brand-300 hover:bg-surface-50'
        }
      `}
    >
      <Icon className={`w-6 h-6 mb-2 ${isActive ? 'text-brand-600' : 'text-surface-500 group-hover:text-brand-500'}`} />
      <div className={`text-sm font-medium ${isActive ? 'text-brand-700' : 'text-surface-900'}`}>{label}</div>
      <div className="text-xs text-surface-500">{sublabel}</div>
    </button>
  )
}

// Candidate chip
interface CandidateChipProps {
  candidate: AddedCandidate
  onRemove: () => void
}

function CandidateChip({ candidate, onRemove }: CandidateChipProps) {
  const sourceIcon = {
    resume: DocumentArrowUpIcon,
    linkedin: LinkIcon,
    manual: UserPlusIcon,
    ats: ArrowPathIcon,
  }
  const SourceIcon = sourceIcon[candidate.source]

  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-surface-50 border border-surface-200 group">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-8 h-8 rounded-lg bg-surface-200 flex items-center justify-center shrink-0">
          <SourceIcon className="w-4 h-4 text-surface-600" />
        </div>
        <div className="min-w-0">
          <div className="font-medium text-surface-900 truncate">{candidate.name}</div>
          <div className="text-xs text-surface-500 truncate">
            {candidate.fileName || candidate.linkedinUrl || candidate.email || 'Manual entry'}
          </div>
        </div>
      </div>
      <button
        onClick={onRemove}
        className="p-1.5 rounded-lg text-surface-400 hover:text-surface-600 hover:bg-surface-200 opacity-0 group-hover:opacity-100 transition-all"
      >
        <XMarkIcon className="w-4 h-4" />
      </button>
    </div>
  )
}

// Step 2: Assign to role
interface AssignStepProps {
  candidates: AddedCandidate[]
  roles: Role[]
  selectedRoleId: string | null
  setSelectedRoleId: (id: string | null) => void
  onBack: () => void
  onComplete: () => void
}

function AssignStep({
  candidates,
  roles,
  selectedRoleId,
  setSelectedRoleId,
  onBack,
  onComplete,
}: AssignStepProps) {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="pr-8">
        <h2 className="text-2xl font-display font-semibold text-surface-900">
          Where should they go?
        </h2>
        <p className="mt-1 text-surface-500">
          Assign {candidates.length} candidate{candidates.length !== 1 ? 's' : ''} to a role, or keep them in your talent pool.
        </p>
      </div>

      {/* Role options */}
      <div className="space-y-3">
        {/* Talent Pool option */}
        <button
          onClick={() => setSelectedRoleId(null)}
          className={`
            w-full p-4 rounded-xl border-2 transition-all duration-200 text-left flex items-center gap-4
            ${selectedRoleId === null 
              ? 'border-brand-500 bg-brand-50 ring-2 ring-brand-100' 
              : 'border-surface-200 hover:border-brand-300 hover:bg-surface-50'
            }
          `}
        >
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${selectedRoleId === null ? 'bg-brand-100' : 'bg-surface-100'}`}>
            <UsersIcon className={`w-6 h-6 ${selectedRoleId === null ? 'text-brand-600' : 'text-surface-500'}`} />
          </div>
          <div className="flex-1">
            <div className={`font-medium ${selectedRoleId === null ? 'text-brand-700' : 'text-surface-900'}`}>
              Talent Pool
            </div>
            <div className="text-sm text-surface-500">
              Keep unassigned for now — evaluate later
            </div>
          </div>
          {selectedRoleId === null && (
            <div className="w-6 h-6 rounded-full bg-brand-600 flex items-center justify-center">
              <CheckIcon className="w-4 h-4 text-white" />
            </div>
          )}
        </button>

        {/* Divider */}
        {roles.length > 0 && (
          <div className="relative py-2">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-surface-200" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-white px-3 text-sm text-surface-400">or assign to a role</span>
            </div>
          </div>
        )}

        {/* Role options */}
        {roles.map(role => (
          <button
            key={role.id}
            onClick={() => setSelectedRoleId(role.id)}
            className={`
              w-full p-4 rounded-xl border-2 transition-all duration-200 text-left flex items-center gap-4
              ${selectedRoleId === role.id 
                ? 'border-brand-500 bg-brand-50 ring-2 ring-brand-100' 
                : 'border-surface-200 hover:border-brand-300 hover:bg-surface-50'
              }
            `}
          >
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${selectedRoleId === role.id ? 'bg-brand-100' : 'bg-surface-100'}`}>
              <BriefcaseIcon className={`w-6 h-6 ${selectedRoleId === role.id ? 'text-brand-600' : 'text-surface-500'}`} />
            </div>
            <div className="flex-1">
              <div className={`font-medium ${selectedRoleId === role.id ? 'text-brand-700' : 'text-surface-900'}`}>
                {role.title}
              </div>
              <div className="text-sm text-surface-500">
                {role.levelRange}
              </div>
            </div>
            {selectedRoleId === role.id && (
              <div className="w-6 h-6 rounded-full bg-brand-600 flex items-center justify-center">
                <CheckIcon className="w-4 h-4 text-white" />
              </div>
            )}
          </button>
        ))}
      </div>

      {/* Actions */}
      <div className="flex justify-between pt-2">
        <button
          onClick={onBack}
          className="px-5 py-2.5 rounded-lg text-surface-600 hover:text-surface-900 hover:bg-surface-100 font-medium transition-colors"
        >
          Back
        </button>
        <button
          onClick={onComplete}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-brand-600 text-white font-medium hover:bg-brand-700 transition-colors shadow-sm"
        >
          Add candidates
          <ArrowRightIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}

// Step 3: Success
interface SuccessStepProps {
  candidates: AddedCandidate[]
  selectedRoleId: string | null
  roles: Role[]
  onClose: () => void
  onAddMore: () => void
}

function SuccessStep({ candidates, selectedRoleId, roles, onClose, onAddMore }: SuccessStepProps) {
  const selectedRole = roles.find(r => r.id === selectedRoleId)
  
  return (
    <div className="text-center space-y-6 py-4">
      {/* Success animation */}
      <div className="relative mx-auto w-20 h-20">
        <div className="absolute inset-0 bg-emerald-100 rounded-full animate-ping opacity-75" />
        <div className="relative w-20 h-20 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center shadow-lg shadow-emerald-500/30">
          <CheckIcon className="w-10 h-10 text-white" />
        </div>
      </div>

      {/* Message */}
      <div className="space-y-2">
        <h2 className="text-2xl font-display font-semibold text-surface-900">
          {candidates.length} candidate{candidates.length !== 1 ? 's' : ''} added
        </h2>
        <p className="text-surface-500">
          {selectedRole 
            ? `Assigned to ${selectedRole.title}` 
            : 'Added to Talent Pool'
          }
        </p>
      </div>

      {/* AI signal indicator */}
      <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-brand-50 to-cyan-50 border border-brand-100">
        <SparklesIcon className="w-5 h-5 text-brand-600" />
        <span className="text-sm font-medium text-surface-700">
          Initial signal extracted. Evaluation can begin.
        </span>
      </div>

      {/* Candidate summary */}
      <div className="flex flex-wrap justify-center gap-2 pt-2">
        {candidates.slice(0, 5).map(candidate => (
          <div 
            key={candidate.id}
            className="px-3 py-1.5 rounded-full bg-surface-100 text-sm text-surface-700"
          >
            {candidate.name}
          </div>
        ))}
        {candidates.length > 5 && (
          <div className="px-3 py-1.5 rounded-full bg-surface-100 text-sm text-surface-500">
            +{candidates.length - 5} more
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row justify-center gap-3 pt-4">
        <button
          onClick={onAddMore}
          className="px-5 py-2.5 rounded-lg text-surface-600 hover:text-surface-900 border border-surface-200 hover:bg-surface-50 font-medium transition-colors"
        >
          Add more candidates
        </button>
        <button
          onClick={onClose}
          className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl bg-brand-600 text-white font-medium hover:bg-brand-700 transition-colors shadow-sm"
        >
          {selectedRole ? 'View role' : 'View talent pool'}
          <ArrowRightIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}

